import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { ThemeToggle } from "./ThemeToggle";
import { NAV_LINKS, LOGO_LIGHT } from "../data/site";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    `relative text-sm font-medium transition-colors duration-200 ${
      isActive
        ? "text-accent"
        : scrolled
          ? "text-foreground/80 hover:text-primary"
          : "text-white/90 hover:text-accent"
    }`;

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/70 dark:bg-[#06281A]/70 backdrop-blur-xl border-b border-border/60 shadow-sm"
          : "bg-transparent"
      }`}
      data-testid="main-navbar"
    >
      <nav className="container-px mx-auto max-w-7xl flex items-center justify-between h-16 lg:h-20">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo-link">
          <span className="inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl bg-white shadow-md ring-1 ring-black/5 transition-transform group-hover:scale-105">
            <img src={LOGO_LIGHT} alt="Domaleo Enterprise" className="h-full w-full object-contain p-0.5" />
          </span>
          <span className="flex flex-col leading-none">
            <span className={`font-heading font-bold text-base sm:text-lg ${scrolled ? "text-foreground" : "text-white"}`}>
              Domaleo
            </span>
            <span className={`text-[10px] uppercase tracking-[0.18em] ${scrolled ? "text-muted-foreground" : "text-white/70"}`}>Enterprise</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-link-${l.label.toLowerCase()}`}>
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <ThemeToggle className="hidden sm:inline-flex" />
          <Link
            to="/contact"
            className="hidden lg:inline-flex items-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-md transition-transform duration-300 hover:scale-105 hover:bg-primary/90"
            data-testid="nav-quote-btn"
          >
            Request a Quote
          </Link>

          <Sheet>
            <SheetTrigger asChild>
              <button
                className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-foreground"
                aria-label="Open menu"
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-background">
              <div className="mt-8 flex flex-col gap-1" data-testid="mobile-menu-panel">
                {NAV_LINKS.map((l) => (
                  <SheetClose asChild key={l.to}>
                    <NavLink
                      to={l.to}
                      className={({ isActive }) =>
                        `rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                          isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                        }`
                      }
                      data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    >
                      {l.label}
                    </NavLink>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <Link
                    to="/contact"
                    className="mt-4 inline-flex items-center justify-center rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
                    data-testid="mobile-quote-btn"
                  >
                    Request a Quote
                  </Link>
                </SheetClose>
                <div className="mt-4 flex items-center gap-3 px-4">
                  <ThemeToggle />
                  <span className="text-sm text-muted-foreground">Toggle theme</span>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
